import { useParams } from '@tanstack/react-router';

import { kinds, isEnvKey } from '@/app/site/scope';
import { useSiteScope } from '@/app/site/useSiteScope';
import { MediaLibrary } from '@/features/media/MediaLibrary';
import type { AssetBody } from '@/shared/api/generated/model';
import { Dialog, DialogContent } from '@/shared/ui/Dialog';

export interface MediaPickerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  siteID: string;
  /** `image` — только картинки, `file` — любой файл. */
  kind: 'image' | 'file';
  chosenID?: string | undefined;
  onChoose: (item: AssetBody) => void;
  /** Выбор нескольких файлов разом — для галереи. */
  onChooseMany?: ((items: AssetBody[]) => void) | undefined;
}

/**
 * Выбор файла для поля записи — та же медиатека, что на экране «Медиа»:
 * с папками, поиском и загрузкой. Открывается в последней папке; новый файл
 * загружается прямо туда, без похода в другой раздел посреди работы.
 */
export function MediaPicker({
  open,
  onOpenChange,
  siteID,
  kind,
  chosenID,
  onChoose,
  onChooseMany,
}: MediaPickerProps) {
  const params = useParams({ strict: false });
  const scope = useSiteScope();
  const envKey = typeof params.env === 'string' && isEnvKey(params.env) ? params.env : 'prod';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {open && (
        <DialogContent
          title={
            onChooseMany !== undefined
              ? kind === 'image'
                ? 'Выбор картинок'
                : 'Выбор файлов'
              : kind === 'image'
                ? 'Выбор картинки'
                : 'Выбор файла'
          }
          size="xl"
        >
          <MediaLibrary
            siteId={siteID}
            env={kinds[envKey]}
            canWrite={scope?.can('content.write') ?? false}
            canRemove={false}
            pick={{
              kind: kind === 'image' ? 'image' : 'any',
              chosenID,
              onChoose: (item) => {
                onChoose(item);
                onOpenChange(false);
              },
              ...(onChooseMany === undefined
                ? {}
                : {
                    onChooseMany: (items: AssetBody[]) => {
                      onChooseMany(items);
                      onOpenChange(false);
                    },
                  }),
            }}
          />
        </DialogContent>
      )}
    </Dialog>
  );
}
